import React, { useState } from 'react';
import { CheckCircle2, Mail } from 'lucide-react';
import { subscribeNewsletter } from '../articlesApi';

/**
 * Inline newsletter signup block. Drop it into any long-form page to give
 * readers a low-friction way to opt in. `source` is a free-text tag we
 * persist on the signup so the admin can see which article converted.
 */
const NewsletterSignup = ({ source, variant = 'article' }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState({ state: 'idle', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: 'submitting', message: '' });
    try {
      await subscribeNewsletter(email, source);
      setStatus({ state: 'done', message: 'Thanks, you\'re on the list.' });
      setEmail('');
    } catch (err) {
      setStatus({ state: 'error', message: err.message });
    }
  };

  if (status.state === 'done') {
    return (
      <div
        className="border border-emerald-200 bg-emerald-50 rounded-xl p-6 flex items-center gap-3 text-emerald-800"
        data-testid={`newsletter-success-${variant}`}
      >
        <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
        <div className="text-sm">
          <span className="font-medium">{status.message}</span>{' '}
          You&rsquo;ll get new pieces the day they publish.
        </div>
      </div>
    );
  }

  return (
    <div
      className="border border-slate-200 bg-white rounded-xl p-6 sm:p-8"
      data-testid={`newsletter-block-${variant}`}
    >
      <div className="flex items-start gap-4">
        <div className="hidden sm:flex w-11 h-11 rounded-lg bg-blue-50 text-blue-600 items-center justify-center flex-shrink-0">
          <Mail className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-2">
            The Keystone brief
          </p>
          <h3 className="text-xl sm:text-2xl font-medium tracking-tight text-slate-900 leading-tight mb-2">
            New insights, delivered when published.
          </h3>
          <p className="text-sm text-slate-600 mb-5">
            Practitioner-led writing on delivery governance, PFI hand-back, Building
            Safety Act evidence and NHS capital business cases. No fluff, no sales pitch.
          </p>
          <form
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-2"
            data-testid={`newsletter-form-${variant}`}
          >
            <input
              type="email"
              required
              autoComplete="email"
              placeholder="you@company.co.uk"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 min-w-0 rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid={`newsletter-input-${variant}`}
              disabled={status.state === 'submitting'}
            />
            <button
              type="submit"
              disabled={status.state === 'submitting'}
              className="inline-flex items-center justify-center bg-slate-900 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-slate-800 disabled:opacity-50 transition-colors"
              data-testid={`newsletter-submit-${variant}`}
            >
              {status.state === 'submitting' ? 'Signing up…' : 'Subscribe'}
            </button>
          </form>
          {status.state === 'error' && (
            <p
              className="mt-3 text-sm text-red-600"
              data-testid={`newsletter-error-${variant}`}
            >
              {status.message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsletterSignup;
